'use client';

import { useCallback, useEffect, useState } from 'react';
import { Link } from '@/i18n/routing';
import { useTranslations } from 'next-intl';
import {
  CONSENT_STORAGE_KEY,
  DENIED_CONSENT,
  type ConsentPreferences,
  readStoredConsent,
  storeConsent,
} from '@/lib/marketing/consent';

const OPEN_COOKIE_SETTINGS_EVENT = 'printen-open-cookie-settings';

function clearMarketingCookies() {
  const names = ['_fbp', '_fbc', '_gcl_au'];
  names.forEach((name) => {
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax; Secure`;
    document.cookie = `${name}=; path=/; domain=.${window.location.hostname}; max-age=0; SameSite=Lax; Secure`;
  });
}

function syncTrackingConsentCookie(enabled: boolean) {
  document.cookie = enabled
    ? 'printen_tracking_consent=1; path=/; max-age=31536000; SameSite=Lax; Secure'
    : 'printen_tracking_consent=; path=/; max-age=0; SameSite=Lax; Secure';
}

function recordAnonymousConsentChoice(next: ConsentPreferences) {
  const eventName = next.analytics && next.marketing && next.preferences
    ? 'consent_accept_all'
    : !next.analytics && !next.marketing && !next.preferences
      ? 'consent_reject_optional'
      : 'consent_custom';
  const randomId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  void fetch('/api/marketing/event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      eventName,
      eventId: `aggregate:consent:${randomId}`,
      privacyMode: 'aggregate',
      url: `${window.location.origin}${window.location.pathname}`,
      locale: document.documentElement.lang || undefined,
      consent: { analytics: false, marketing: false, preferences: false },
    }),
  }).catch(() => undefined);
}

export function CookieConsent() {
  const t = useTranslations('cookies');
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [choices, setChoices] = useState<ConsentPreferences>({ ...DENIED_CONSENT });

  const openSettings = useCallback(() => {
    const stored = readStoredConsent(window.localStorage);
    setChoices(stored ? {
      analytics: stored.analytics,
      marketing: stored.marketing,
      preferences: stored.preferences,
    } : { ...DENIED_CONSENT });
    setShowDetails(Boolean(stored));
    setShowBanner(true);
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      if (!window.localStorage.getItem(CONSENT_STORAGE_KEY)) {
        timer = setTimeout(() => setShowBanner(true), 350);
      }
    } catch {
      timer = setTimeout(() => setShowBanner(true), 350);
    }

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    };
  }, [openSettings]);

  const save = (next: ConsentPreferences) => {
    recordAnonymousConsentChoice(next);
    try {
      storeConsent(window.localStorage, next);
    } catch {
      // A blocked storage API must never prevent the visitor from using the shop.
    }

    syncTrackingConsentCookie(next.analytics || next.marketing);
    if (!next.marketing) clearMarketingCookies();
    window.dispatchEvent(new CustomEvent('printen-consent-updated', { detail: next }));
    setChoices(next);
    setShowBanner(false);
    setShowDetails(false);
  };

  if (!showBanner) return null;

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      className="fixed bottom-3 left-3 right-3 z-[100000] max-h-[calc(100dvh-1.5rem)] overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl sm:bottom-6 sm:left-1/2 sm:right-auto sm:max-h-[calc(100dvh-3rem)] sm:w-[min(720px,calc(100%-3rem))] sm:-translate-x-1/2 sm:p-6"
    >
      <div className="space-y-4">
        <div>
          <h2 id="cookie-consent-title" className="text-base font-black text-slate-950">
            {t('banner.title')}
          </h2>
          <p id="cookie-consent-description" className="mt-1 text-sm leading-6 text-slate-600">
            {t('banner.description')}{' '}
            <Link href="/cookies" className="font-semibold text-blue-700 underline-offset-2 hover:underline">
              {t('banner.readMore')}
            </Link>
          </p>
        </div>

        {showDetails && (
          <div className="space-y-2 rounded-xl bg-slate-50 p-3" aria-label={t('banner.settings')}>
            <ConsentRow label={t('banner.necessary')} description={t('banner.necessaryDescription')} checked disabled onChange={() => undefined} />
            <ConsentRow
              label={t('banner.preferences')}
              description={t('banner.preferencesDescription')}
              checked={choices.preferences}
              onChange={(checked) => setChoices((current) => ({ ...current, preferences: checked }))}
            />
            <ConsentRow
              label={t('banner.analytics')}
              description={t('banner.analyticsDescription')}
              checked={choices.analytics}
              onChange={(checked) => setChoices((current) => ({ ...current, analytics: checked }))}
            />
            <ConsentRow
              label={t('banner.marketing')}
              description={t('banner.marketingDescription')}
              checked={choices.marketing}
              onChange={(checked) => setChoices((current) => ({ ...current, marketing: checked }))}
            />
          </div>
        )}

        <div className="grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => save({ analytics: true, marketing: true, preferences: true })}
            className="min-h-11 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-2"
          >
            {t('banner.acceptAll')}
          </button>
          <button
            type="button"
            onClick={() => save({ ...DENIED_CONSENT })}
            className="min-h-11 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-2"
          >
            {t('banner.rejectOptional')}
          </button>
          {showDetails ? (
            <button
              type="button"
              onClick={() => save(choices)}
              className="min-h-11 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-bold text-blue-800 transition hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-2 sm:col-span-2"
            >
              {t('banner.save')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              className="min-h-11 rounded-xl px-4 py-2.5 text-sm font-bold text-blue-800 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-2 sm:col-span-2"
            >
              {t('banner.settings')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function ConsentRow({
  label,
  description,
  checked,
  disabled = false,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-start justify-between gap-4 rounded-lg bg-white p-3 shadow-sm ring-1 ring-slate-200 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-700">
      <span>
        <span className="block text-sm font-bold text-slate-900">{label}</span>
        <span className="mt-0.5 block text-xs leading-5 text-slate-600">{description}</span>
      </span>
      <input
        type="checkbox"
        className="mt-1 h-5 w-5 shrink-0 accent-blue-700"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}
